// Styled bottom-sheet replacement for window.prompt(). The native prompt is
// (a) unstyled and breaks the brand on iOS PWAs, (b) unreachable for VoiceOver
// rotor navigation, (c) explicitly flagged by Apple reviewers. This shares
// chrome with QuantityConfirm so the UX stays consistent.

import { useEffect, useRef, useState, type FormEvent } from "react";
import { X } from "lucide-react";

interface Props {
  title: string;
  description?: string;
  defaultValue?: string;
  placeholder?: string;
  confirmLabel?: string;
  /** Resolved with the trimmed input on confirm, or null on cancel. */
  onClose: (value: string | null) => void;
  maxLength?: number;
}

export default function PromptModal({
  title,
  description,
  defaultValue = "",
  placeholder,
  confirmLabel = "Save",
  onClose,
  maxLength = 100
}: Props) {
  const [value, setValue] = useState(defaultValue);
  const inputRef = useRef<HTMLInputElement>(null);

  // Autofocus + select the suggested name so a typed character replaces it.
  useEffect(() => {
    const t = setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 50);
    return () => clearTimeout(t);
  }, []);

  // Escape to cancel — table-stakes for any modal.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose(null);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  function submit(e: FormEvent) {
    e.preventDefault();
    const trimmed = value.trim();
    if (trimmed) onClose(trimmed);
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/70 z-50 backdrop-blur-sm" onClick={() => onClose(null)} />
      <div
        className="fixed inset-x-0 bottom-0 z-50 bg-slate-900 border-t border-slate-800 rounded-t-2xl shadow-2xl"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="prompt-modal-title"
      >
        <div className="max-w-md md:max-w-2xl lg:max-w-3xl mx-auto p-4 space-y-4">
          <div className="flex items-center justify-between relative">
            <div className="h-1 w-10 rounded-full bg-slate-700 mx-auto" />
            <button
              type="button"
              onClick={() => onClose(null)}
              className="absolute right-0 w-8 h-8 rounded-full text-slate-400 hover:bg-slate-800 flex items-center justify-center"
              aria-label="Cancel"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <form onSubmit={submit} className="space-y-3">
            <div>
              <label id="prompt-modal-title" htmlFor="prompt-modal-input" className="text-base font-semibold text-slate-100">
                {title}
              </label>
              {description && (
                <div className="text-xs text-slate-400 mt-0.5">{description}</div>
              )}
            </div>

            <input
              id="prompt-modal-input"
              ref={inputRef}
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={placeholder}
              maxLength={maxLength}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500"
            />

            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={() => onClose(null)}
                className="flex-1 py-3 rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!value.trim()}
                className="flex-1 py-3 rounded-lg bg-emerald-500 text-slate-950 font-semibold disabled:opacity-50"
              >
                {confirmLabel}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
